import hopscotch from 'hopscotch';
import flatten from 'lodash/flatten';
import { HelpDetails } from 'app/shared/help_and_information/HelpRegistry';
import { ImsItemPanelService } from 'app/shared/ims_items/ImsItemPanelService';
import { Toast } from 'app/shared/toast/toast.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { k_SHOW_MAP, k_SHOW_OVERVIEW, PanesService } from 'app/main/PanesService';
import { OverviewService } from 'app/overview/OverviewService';
import { ImsPanelService } from 'app/shared/ims_panel.service';
import { CenterPanelService } from 'app/center/center_panel.service';
import { NavTree, NavResult } from 'app/shared/internal_navigation/nav';
import { Ticket } from 'app/shared/collections/ticket';
import { onSiteChangeClosePanel } from 'app/shared/ims_panel_utils';
import { SiteStateService } from 'app/shared/site_state.service';
import { AssetTemplate } from 'app/shared/collections/asset_template/asset_template';

export const PLACEMENT_TOP: placementTypes = 'top';
export const PLACEMENT_RIGHT: placementTypes = 'right';
export const PLACEMENT_BOTTOM: placementTypes = 'bottom';
export const PLACEMENT_LEFT: placementTypes = 'left';

export const TOUR_TYPE__IMS_ITEM_PANEL = 'tour_type__ims_item';
// A tour for an overview tab. This tour type will navigate to the overview
// pane and then to the requested overview tab
export const TOUR_TYPE__OVERVIEW_TAB = 'tour_type__overview_tab';
// A tour that navigates to a position in the app and then starts the tour.
// A more generic version of the ims item panel and overview tab tours.
export const TOUR_TYPE__NAVIGATED_TOUR = 'tour_type__navigated';
export const TOUR_TYPE__CENTER_PANEL = 'tour_type__center_panel';

interface PanelTabInnerStep extends Partial<StepDefinition> {
    key: string;
}

export interface PanelTabTour {
    tabKey: string;
    title: string;
    content: string;
    innerSteps: Array<PanelTabInnerStep>;
    ignoreTabStep?: boolean;
}

const defaultStepDefinition = Object.freeze({
    fixedElement: true,
});

const TOUR_RETRY_COUNT = 5;
const TOUR_RETRY_TIME = 1000; // in milliseconds
const retryTour = (retryCount) => {
    return () => {
        // If the first tour step fails
        if (hopscotch.getCurrStepNum() == 0) {
            const currentTour = hopscotch.getCurrTour();
            // Take a reference to onEnd / onClose functions, we should only run them when our retries are done.
            const realOnClose = currentTour.onClose;
            const realOnEnd = currentTour.onEnd;
            const cleanUp = () => {
                typeof realOnClose == 'function' ? realOnClose() : () => {};
                typeof realOnEnd == 'function' ? realOnEnd() : () => {};
            };
            // If we have not hit our retry limit, schedule a retry.
            if (retryCount < TOUR_RETRY_COUNT) {
                const newCount = retryCount + 1;
                // Remove any cleanup from current tours, this should only be run when retries fail.
                currentTour.onClose = () => {};
                currentTour.onEnd = () => {};

                // Create a new reference to the tour, currentTour will get called by hopscotch.
                const newTour = Object.assign({}, currentTour, {
                    onEnd: realOnEnd,
                    onClose: realOnClose,
                    onError: retryTour(newCount),
                });

                // Start a new tour with the real on end / on close functions.
                setTimeout(() => {
                    // If a different tour has been started, clean this one up.
                    if (hopscotch.getCurrTour()) {
                        return cleanUp();
                    }
                    // Retry!
                    TourService.startNewTour(newTour);
                }, TOUR_RETRY_TIME);
            } else {
                // We have retried too many times, clean up.
                new Toast(Toast.ERROR, 'Unable to start tour');
                cleanUp();
            }
            // Stop current tour from running on the first error.
            // This is to prevent starting a tour if the proceeding steps have elements but the first step does not.
            hopscotch.endTour(true);
        }
    };
};

const defaultTourDefinition = Object.freeze({
    bubbleWidth: 300,
    // It's actually quite common for the tour step target to not be found in the case
    // of dynamic forms not having certain elements in the DOM. When debugging you may
    // find it useful to put a console.log or console.error in this function.
    onError: retryTour(0),
});

export class TourService {
    private _siteStateService: SiteStateService;
    private _imsPanelService: ImsPanelService;
    private _imsItemPanelService: ImsItemPanelService;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;
    private _panesService: PanesService;
    private _overviewService: OverviewService;
    private _centerPanelService: CenterPanelService;
    private _navTree: NavTree;

    constructor(
        siteStateService: SiteStateService,
        imsPanelService: ImsPanelService,
        imsItemPanelService: ImsItemPanelService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        panesService: PanesService,
        overviewService: OverviewService,
        centerPanelService: CenterPanelService,
    ) {
        this._siteStateService = siteStateService;
        this._imsPanelService = imsPanelService;
        this._imsItemPanelService = imsItemPanelService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._panesService = panesService;
        this._overviewService = overviewService;
        this._centerPanelService = centerPanelService;

        this._navTree = new NavTree(panesService, overviewService);
    }

    static endCurrentTour(): void {
        hopscotch.endTour(true);
    }

    static startNewTour(tour: TourDefinition): void {
        TourService.endCurrentTour();
        // Timeout seems to improve animation performance of first tour step
        setTimeout(() => {
            // It's possible that part of starting a tour a tour will end.
            // When a tour ends it still tries to go through the remaining steps
            // This prevents an unneccesary error 'unable to read steps of undefined', undefined being the 'tour'
            try {
                hopscotch.startTour(tour);
            } catch (err) {
                if (
                    err instanceof TypeError &&
                    (err.message.includes(`Cannot read property 'steps' of null`) ||
                        err.message.includes(`Cannot read properties of null (reading 'steps')`))
                ) {
                    return;
                }
                throw err;
            }
        });
    }

    static resetTourBubbleLocation(): void {
        setTimeout(() => {
            // If we have a tours bubbles to reset the position of.
            if (hopscotch.getCurrTour()) {
                (hopscotch as any).refreshBubblePosition();
            }
        }, 0);
    }

    // very brittle, relies on having the same structure as imsItemPanelTourIdString
    static imsItemPanelTourStepIdFn(
        imsItemType: string,
        idExpression: string,
        stepKey: string,
    ): string {
        return `${idExpression}_${imsItemType}_${stepKey}`;
    }

    // very brittle, relies on having the same structure as imsItemPanelTourStepIdFn
    static imsItemPanelTourIdString(imsItemType: string, imsId: number, stepKey: string): string {
        return `${imsId}_${imsItemType}_${stepKey}`;
    }

    private _buildImsItemPanelTour(helpDetails: HelpDetails, imsId: number): TourDefinition {
        const imsItemType = helpDetails.key;

        const buildTargetString = (stepKey) =>
            TourService.imsItemPanelTourIdString(imsItemType, imsId, stepKey);

        const tabSteps = helpDetails.tabbedPanelTour.map(
            ({ tabKey, title, content, innerSteps, ignoreTabStep }, index) => {
                const firstStep = Object.assign({}, defaultStepDefinition, {
                    target: buildTargetString(tabKey),
                    title,
                    content,
                    placement: PLACEMENT_BOTTOM,
                    xOffset: -10,
                    onShow:
                        index === 0
                            ? () => {
                                  this._imsItemPanelService.tourStarted(imsItemType, imsId);
                                  this._imsItemPanelService.setActiveTab(
                                      imsItemType,
                                      imsId,
                                      tabKey,
                                      true,
                                  );
                              }
                            : () =>
                                  this._imsItemPanelService.setActiveTab(
                                      imsItemType,
                                      imsId,
                                      tabKey,
                                      true,
                                  ),
                });

                const remainingSteps = innerSteps.map((innerStep) =>
                    Object.assign(
                        {},
                        defaultStepDefinition,
                        {
                            target: buildTargetString(innerStep.key),
                        },
                        innerStep,
                    ),
                );
                // If we're ignoring the tab step, do not include it.
                return !ignoreTabStep ? [firstStep, ...remainingSteps] : [...remainingSteps];
            },
        );

        return Object.assign({}, defaultTourDefinition, {
            id: imsItemType,
            steps: flatten<any>(tabSteps),
            onStart: helpDetails.onStart,
            onEnd: () => {
                if (helpDetails.onEnd) {
                    helpDetails.onEnd();
                }
                this._imsItemPanelService.tourFinished();
            },
            onClose: () => {
                if (helpDetails.onClose) {
                    helpDetails.onClose();
                }
                this._imsItemPanelService.tourFinished();
            },
        });
    }

    private async _startImsItemPanelTour(helpDetails: HelpDetails): Promise<void> {
        const imsItemType = helpDetails.key;

        // Create a mocked IMS Object in the collection.
        const { collection, ItemClass } = this._imsItemTypeRegistry.getTypeDetails(imsItemType);
        const imsObject = collection.addLocalObject(new ItemClass());
        this._mockImsItemForTour(imsObject);

        // Open a panel with the mocked object
        const tourPanelApi = await this._imsItemPanelService.openTourPanelForType(
            imsItemType,
            imsObject.id,
        );

        // HACK - When the site changes, just close this panel. The collection we've added
        //  a 'local' object to is probably going to get cleared on site change,
        //  and the panel component itself is probably pulling the object from the collection too.
        //  If the site changes, the local object will be obliterated from the collection and probably cause an error
        //  in the panel components from a lack of null checks.
        onSiteChangeClosePanel(tourPanelApi.api, this._siteStateService);

        // Once our tour is done, remove the mock object and close the panel
        // hasBeenClosed is to prevent an error when this gets called twice on close.
        let hasBeenClosed = false;
        const closeTourAndPanel = async () => {
            if (hasBeenClosed) {
                return;
            }
            hasBeenClosed = true;
            await collection.removeObject(imsObject);
            await this._imsItemPanelService.state.effects.close(imsItemType, imsObject.id);
        };

        // We add it to onClose and onEnd as a tour is not closed when it ends, but it ends when it's closed.
        helpDetails.onClose = closeTourAndPanel;
        helpDetails.onEnd = closeTourAndPanel;

        // Start the tour on the mocked object
        TourService.startNewTour(this._buildImsItemPanelTour(helpDetails, imsObject.id));
    }

    private _mockImsItemForTour(imsObject) {
        // Set mock name for the tour
        if (imsObject instanceof Ticket) {
            // don't mind me, I'm just a ticket without a name property, doh
            imsObject.subject = 'Unknown';
        } else if (imsObject instanceof AssetTemplate) {
            imsObject.templateName = 'Sample Asset Template';
            imsObject.assetTypeId = 1;
        } else {
            imsObject.name = 'Unknown';
        }
    }

    static overviewTabBtnId(tabKey): string {
        return `overview_tab__${tabKey}`;
    }

    private _startOverviewTabTour(helpDetails: HelpDetails): void {
        const tabKey = helpDetails.key;

        const delay = this._panesService.state.getState()[k_SHOW_OVERVIEW] ? 0 : 1000;

        this._overviewService.state.effects.setOverviewTab(tabKey);

        this._panesService.state.effects.setVisiblePanes({
            [k_SHOW_MAP]: false,
            [k_SHOW_OVERVIEW]: true,
        });

        TourService.startNewTour(
            Object.assign({}, defaultTourDefinition, {
                id: tabKey,
                steps: [
                    Object.assign({}, defaultStepDefinition, {
                        title: `${helpDetails.header} Tab`,
                        content: '',
                        target: TourService.overviewTabBtnId(tabKey),
                        placement: PLACEMENT_BOTTOM,
                        delay, // wait for panes to move
                    }),
                    ...helpDetails.innerTourSteps.map((stepDefinition) =>
                        Object.assign({}, defaultStepDefinition, stepDefinition),
                    ),
                ],
                onStart: helpDetails.onStart,
                onEnd: helpDetails.onEnd,
                onClose: helpDetails.onClose,
            }),
        );
    }

    /**
     * Navigate to a particular spot in the application, then start the tour
     */
    private _startNavigatedTour(helpDetails: HelpDetails): void {
        const key = helpDetails.key;

        let delay;
        let targetSuffix;
        if (helpDetails.navigateTo) {
            const navResult: NavResult = this._navTree.navigate(helpDetails.navigateTo);
            targetSuffix = navResult.targetSuffix;
            delay = navResult.delay;
            delay += helpDetails.beforeStart ? helpDetails.beforeStart() : 0;
        } else {
            delay = 0;
            targetSuffix = null;
        }

        setTimeout(() => {
            TourService.startNewTour(
                Object.assign({}, defaultTourDefinition, {
                    id: key,
                    steps: [
                        ...helpDetails.innerTourSteps.map((stepDefinition) =>
                            Object.assign(
                                {},
                                defaultStepDefinition,
                                stepDefinition,
                                targetSuffix
                                    ? { target: `${stepDefinition.target}${targetSuffix}` }
                                    : {},
                            ),
                        ),
                    ],
                    onStart: helpDetails.onStart,
                    onEnd: helpDetails.onEnd,
                    onClose: helpDetails.onClose,
                }),
            );
        }, delay);
    }

    static centerPanelHeaderId(tag): string {
        return `${tag}__header`;
    }

    static centerPanelDockBtnId(tag): string {
        return `${tag}__dock_btn`;
    }

    private _startCenterPanelTour(helpDetails: HelpDetails): void {
        const panelApi = this._imsPanelService.getPanelApiByTag(helpDetails.key);

        if (!panelApi) {
            new Toast(
                Toast.INFO,
                `${helpDetails.header} needs to be present in the active view to start a tour`,
            );
        } else {
            panelApi.uncollapse();
            panelApi.moveToPane('detached');

            setTimeout(
                () => {
                    let delay = 0;
                    delay += helpDetails.beforeStart ? helpDetails.beforeStart() : 0;

                    setTimeout(() => {
                        TourService.startNewTour(
                            Object.assign({}, defaultTourDefinition, {
                                id: helpDetails.key,
                                onStart: () =>
                                    this._centerPanelService.state.effects.setActiveTour(
                                        helpDetails.key,
                                    ),
                                onEnd: () =>
                                    this._centerPanelService.state.effects.setActiveTour(null),
                                onClose: () =>
                                    this._centerPanelService.state.effects.setActiveTour(null),
                                steps: [
                                    {
                                        target: TourService.centerPanelHeaderId(helpDetails.key),
                                        title: helpDetails.header,
                                        xOffset: -13,
                                        placement: PLACEMENT_BOTTOM,
                                    },
                                    ...helpDetails.innerTourSteps,
                                    {
                                        target: TourService.centerPanelDockBtnId(helpDetails.key),
                                        title: 'Dock Button',
                                        content: `<p>Pressing the dock button will send the 
                                        <span class="ims-name">${helpDetails.header}</span> back to the center pane.</p>`,
                                        placement: PLACEMENT_BOTTOM,
                                        xOffset: -23,
                                    },
                                ],
                            }),
                        );
                    }, delay);
                },
                // Add a 100ms wait before the tour starts clicking around just to make sure everything in the panel has loaded
                100,
            );
        }
    }

    startTour(helpDetails: HelpDetails): void {
        const type = helpDetails.tourType;
        if (type === TOUR_TYPE__IMS_ITEM_PANEL) {
            this._startImsItemPanelTour(helpDetails);
        } else if (type === TOUR_TYPE__OVERVIEW_TAB) {
            this._startOverviewTabTour(helpDetails);
        } else if (type === TOUR_TYPE__CENTER_PANEL) {
            this._startCenterPanelTour(helpDetails);
        } else if (type === TOUR_TYPE__NAVIGATED_TOUR) {
            this._startNavigatedTour(helpDetails);
        } else {
            throw new Error(`No tour handler for type '${type}'`);
        }
    }
}
